import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Paper,
  MenuItem,
  Tab,
  Tabs,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { PeopleOutlined, AddCircleOutline, EmailOutlined, EditOutlined, BlockOutlined, ChevronLeft, ChevronRight, DeleteOutline } from '@mui/icons-material';
import { getApiUrl } from '../utils/apiConfig';
import { apiService } from '../services/api';
import { buildMonthGrid, WOCHENTAGE_KURZ, monatsLabel, todayIso, formatZeit } from '../utils/dienstplanKalender';

const emptyForm = {
  vorname: '',
  nachname: '',
  personalnummer: '',
  datev_personalnummer: '',
  einladung_email: '',
  planner_ressource: '',
  eintrittsdatum: '',
  austrittsdatum: '',
  stundenlohn_cent: '',
  wochenstunden: 40,
  arbeitstage_pro_woche: 5,
  urlaubstage_pro_jahr: 20,
  beschaeftigungsart: 'minijob',
  zeiterfassung_pin: '',
  aktiv: true,
};

const getMinimumVacationDays = (arbeitstage) => {
  const workDays = Number(String(arbeitstage).replace(',', '.'));
  if (!Number.isFinite(workDays) || workDays <= 0) return 20;
  return Math.round(workDays * 4 * 10) / 10;
};

const UserManagement = () => {
  const api = useMemo(() => axios.create({ withCredentials: true }), []);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [profiles, setProfiles] = useState([]);
  // { email, url, expiresAt } – nach Einladung zum Teilen per WhatsApp/SMS
  const [inviteLink, setInviteLink] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [activeTab, setActiveTab] = useState(0);
  const [freigaben, setFreigaben] = useState([]);
  const [freigabenLoading, setFreigabenLoading] = useState(false);
  const [freigabenError, setFreigabenError] = useState('');
  const [abrechnungsMonat, setAbrechnungsMonat] = useState(() => new Date().toISOString().slice(0, 7));
  const [abrechnungsProfilId, setAbrechnungsProfilId] = useState('');
  const [abrechnungsVorschau, setAbrechnungsVorschau] = useState(null);
  const [abrechnungsLoading, setAbrechnungsLoading] = useState(false);
  const [abrechnungsError, setAbrechnungsError] = useState('');
  const [abrechnungSaving, setAbrechnungSaving] = useState(false);
  const [urlaubstageManuellBearbeitet, setUrlaubstageManuellBearbeitet] = useState(false);
  const [scanLinks, setScanLinks] = useState([]);
  const [scanLinksLoading, setScanLinksLoading] = useState(false);
  const [scanLinksError, setScanLinksError] = useState('');
  const [copiedLink, setCopiedLink] = useState('');
  const [dienstplanSchichten, setDienstplanSchichten] = useState([]);
  const [dienstplanLoading, setDienstplanLoading] = useState(false);
  const [dienstplanError, setDienstplanError] = useState('');
  const [dienstplanLink, setDienstplanLink] = useState('');
  const [dienstplanMonat, setDienstplanMonat] = useState(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  });
  // { datum, form: { schichtId|null, ort, zeitVon, zeitBis, maxPlaetze, notiz } }
  const [tagDialog, setTagDialog] = useState(null);
  const [tagSaving, setTagSaving] = useState(false);

  const leeresSchichtForm = { schichtId: null, ort: '', zeitVon: '', zeitBis: '', maxPlaetze: '', notiz: '' };

  const loadProfiles = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get(getApiUrl('/api/mitarbeiter'));
      if (!data?.success) {
        throw new Error(data?.error || 'Fehler beim Laden');
      }
      setProfiles(data.mitarbeiter || []);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Mitarbeiter konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    loadProfiles();
  }, [loadProfiles]);

  useEffect(() => {
    if (activeTab !== 1) return;

    let cancelled = false;
    const loadFreigaben = async () => {
      setFreigabenLoading(true);
      setFreigabenError('');
      try {
        const result = await apiService.getZeitfreigaben();
        if (!cancelled) {
          setFreigaben(result?.eintraege || []);
        }
      } catch (err) {
        if (!cancelled) {
          setFreigabenError(err?.response?.data?.error || err?.message || 'Freigaben konnten nicht geladen werden');
        }
      } finally {
        if (!cancelled) {
          setFreigabenLoading(false);
        }
      }
    };

    loadFreigaben();
    return () => {
      cancelled = true;
    };
  }, [activeTab]);

  useEffect(() => {
    if (activeTab !== 3) return;

    let cancelled = false;
    const loadScanLinks = async () => {
      setScanLinksLoading(true);
      setScanLinksError('');
      try {
        const { data } = await api.get(getApiUrl('/api/mitarbeiter/scan-links'));
        if (!data?.success) {
          throw new Error(data?.error || 'Scan-Links konnten nicht geladen werden');
        }
        if (!cancelled) {
          setScanLinks(data.tasks || []);
        }
      } catch (err) {
        if (!cancelled) {
          setScanLinksError(err?.response?.data?.error || err?.message || 'Scan-Links konnten nicht geladen werden');
        }
      } finally {
        if (!cancelled) {
          setScanLinksLoading(false);
        }
      }
    };

    loadScanLinks();
    return () => {
      cancelled = true;
    };
  }, [activeTab, api]);

  const toAbsoluteScanUrl = (pathOrUrl) => {
    if (!pathOrUrl) return '';
    if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
    return `${window.location.origin}${pathOrUrl}`;
  };

  const handleCopyScanLink = async (url, key) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedLink(key);
      setTimeout(() => setCopiedLink(''), 2000);
    } catch {
      setScanLinksError('Link konnte nicht kopiert werden');
    }
  };

  const loadDienstplan = useCallback(async () => {
    setDienstplanLoading(true);
    setDienstplanError('');
    try {
      const [tageRes, linkRes] = await Promise.all([
        api.get(getApiUrl('/api/mitarbeiter/dienstplan')),
        api.get(getApiUrl('/api/mitarbeiter/dienstplan-link'))
      ]);
      if (!tageRes.data?.success) throw new Error(tageRes.data?.error || 'Dienstplan konnte nicht geladen werden');
      setDienstplanSchichten(tageRes.data.schichten || []);
      if (linkRes.data?.success) setDienstplanLink(linkRes.data.url || linkRes.data.path || '');
    } catch (err) {
      setDienstplanError(err?.response?.data?.error || err?.message || 'Dienstplan konnte nicht geladen werden');
    } finally {
      setDienstplanLoading(false);
    }
  }, [api]);

  useEffect(() => {
    if (activeTab !== 4) return;
    loadDienstplan();
  }, [activeTab, loadDienstplan]);

  const handleSchichtSpeichern = async () => {
    if (!tagDialog) return;
    const { form } = tagDialog;
    if (!form.ort.trim()) {
      setDienstplanError('Bitte einen Ort angeben.');
      return;
    }
    if (!form.zeitVon) {
      setDienstplanError('Bitte eine Startzeit angeben.');
      return;
    }
    setTagSaving(true);
    setDienstplanError('');
    try {
      const payload = {
        datum: tagDialog.datum,
        ort: form.ort.trim(),
        zeit_von: form.zeitVon || null,
        zeit_bis: form.zeitBis || null,
        max_plaetze: form.maxPlaetze === '' ? null : Number(form.maxPlaetze),
        notiz: form.notiz || null
      };
      const { data } = form.schichtId
        ? await api.put(getApiUrl(`/api/mitarbeiter/dienstplan/schichten/${form.schichtId}`), payload)
        : await api.post(getApiUrl('/api/mitarbeiter/dienstplan/schichten'), payload);
      if (!data?.success) throw new Error(data?.error || 'Schicht konnte nicht gespeichert werden');
      setTagDialog((prev) => (prev ? { ...prev, form: { ...leeresSchichtForm } } : prev));
      await loadDienstplan();
    } catch (err) {
      setDienstplanError(err?.response?.data?.error || err?.message || 'Schicht konnte nicht gespeichert werden');
    } finally {
      setTagSaving(false);
    }
  };

  const handleSchichtLoeschen = async (schicht) => {
    const anmeldungen = schicht.anmeldungen?.length || 0;
    if (anmeldungen > 0 && !window.confirm(`Es gibt ${anmeldungen} Anmeldung(en) für diese Schicht. Trotzdem löschen? Die Anmeldungen werden entfernt.`)) {
      return;
    }
    setTagSaving(true);
    setDienstplanError('');
    try {
      const { data } = await api.delete(getApiUrl(`/api/mitarbeiter/dienstplan/schichten/${schicht.schicht_id}`));
      if (!data?.success) throw new Error(data?.error || 'Schicht konnte nicht entfernt werden');
      setTagDialog((prev) => (prev && prev.form.schichtId === schicht.schicht_id ? { ...prev, form: { ...leeresSchichtForm } } : prev));
      await loadDienstplan();
    } catch (err) {
      setDienstplanError(err?.response?.data?.error || err?.message || 'Schicht konnte nicht entfernt werden');
    } finally {
      setTagSaving(false);
    }
  };

  useEffect(() => {
    if (activeTab !== 2 || !abrechnungsProfilId || !abrechnungsMonat) {
      return;
    }

    let cancelled = false;
    const loadVorschau = async () => {
      setAbrechnungsLoading(true);
      setAbrechnungsError('');
      try {
        const result = await apiService.getGehaltsVorschau({ profil_id: abrechnungsProfilId, monat: abrechnungsMonat });
        if (!cancelled) {
          setAbrechnungsVorschau(result || null);
        }
      } catch (err) {
        if (!cancelled) {
          setAbrechnungsVorschau(null);
          setAbrechnungsError(err?.response?.data?.error || err?.message || 'Abrechnungsvorschau konnte nicht geladen werden');
        }
      } finally {
        if (!cancelled) {
          setAbrechnungsLoading(false);
        }
      }
    };

    loadVorschau();
    return () => {
      cancelled = true;
    };
  }, [activeTab, abrechnungsProfilId, abrechnungsMonat]);

  useEffect(() => {
    if (!selectedId) {
      setForm(emptyForm);
      setUrlaubstageManuellBearbeitet(false);
      return;
    }
    const selected = profiles.find((item) => Number(item.profil_id) === Number(selectedId));
    if (!selected) return;
    setForm({
      vorname: selected.vorname || '',
      nachname: selected.nachname || '',
      personalnummer: selected.personalnummer || '',
      datev_personalnummer: selected.datev_personalnummer || '',
      einladung_email: selected.einladung_email || selected.login_email || '',
      planner_ressource: selected.planner_ressource || '',
      eintrittsdatum: selected.eintrittsdatum || '',
      austrittsdatum: selected.austrittsdatum || '',
      stundenlohn_cent: selected.stundenlohn_cent ?? '',
      wochenstunden: selected.wochenstunden ?? 40,
      arbeitstage_pro_woche: selected.arbeitstage_pro_woche ?? 5,
      urlaubstage_pro_jahr: selected.urlaubstage_pro_jahr ?? getMinimumVacationDays(selected.arbeitstage_pro_woche ?? 5),
      beschaeftigungsart: selected.beschaeftigungsart || 'minijob',
      zeiterfassung_pin: selected.zeiterfassung_pin || '',
      aktiv: selected.aktiv !== false,
    });
    setUrlaubstageManuellBearbeitet(true);
  }, [selectedId, profiles]);

  const handleChange = (field) => (event) => {
    const value = event.target.value;

    if (field === 'arbeitstage_pro_woche') {
      setForm((prev) => {
        const nextMin = getMinimumVacationDays(value);
        const currentUrlaub = Number(String(prev.urlaubstage_pro_jahr).replace(',', '.'));
        const nextUrlaub = urlaubstageManuellBearbeitet
          ? (Number.isFinite(currentUrlaub) ? Math.max(currentUrlaub, nextMin) : nextMin)
          : nextMin;
        return {
          ...prev,
          arbeitstage_pro_woche: value,
          urlaubstage_pro_jahr: String(nextUrlaub)
        };
      });
      return;
    }

    if (field === 'urlaubstage_pro_jahr') {
      setUrlaubstageManuellBearbeitet(true);
      setForm((prev) => {
        const nextMin = getMinimumVacationDays(prev.arbeitstage_pro_woche);
        const parsed = Number(String(value).replace(',', '.'));
        const clamped = Number.isFinite(parsed) ? Math.max(parsed, nextMin) : nextMin;
        return {
          ...prev,
          urlaubstage_pro_jahr: String(clamped)
        };
      });
      return;
    }

    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload = {
        ...form,
        stundenlohn_cent: form.stundenlohn_cent,
        wochenstunden: Number(form.wochenstunden),
        arbeitstage_pro_woche: Number(form.arbeitstage_pro_woche),
        urlaubstage_pro_jahr: Number(form.urlaubstage_pro_jahr),
      };

      if (selectedId) {
        await api.put(getApiUrl(`/api/mitarbeiter/${selectedId}`), payload);
      } else {
        await api.post(getApiUrl('/api/mitarbeiter'), payload);
      }

      setSelectedId(null);
      setForm(emptyForm);
      setUrlaubstageManuellBearbeitet(false);
      await loadProfiles();
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Mitarbeiter konnte nicht gespeichert werden');
    } finally {
      setSaving(false);
    }
  };

  // Platzhalter-Adresse: Einladung ohne echte MA-E-Mail, MA registriert sich mit eigener Adresse
  const PLATZHALTER_EMAIL = 'info@immo-automation.de';

  const handleInvite = async (profilId, presetEmail) => {
    const selected = profiles.find((item) => Number(item.profil_id) === Number(profilId));
    const email = presetEmail
      || selected?.einladung_email
      || window.prompt(`E-Mail-Adresse für den MA-Login (oder ${PLATZHALTER_EMAIL} als Platzhalter):`, selected?.login_email || '');
    if (!email) return;

    setSaving(true);
    setError('');
    try {
      const { data } = await api.post(getApiUrl(`/api/mitarbeiter/${profilId}/einladen`), { email });
      const invitation = data?.invitation;
      if (invitation?.invitation_token) {
        setInviteLink({
          email: invitation.email || email,
          url: `${window.location.origin}/register?invitation_token=${encodeURIComponent(invitation.invitation_token)}`,
          expiresAt: invitation.expires_at || null,
          error: null,
        });
      }
      await loadProfiles();
    } catch (err) {
      // Fehler im Dialog statt nur im Seiten-Alert – sonst leicht zu übersehen
      setInviteLink({ email, url: '', expiresAt: null, error: err?.response?.data?.error || err?.message || 'Einladung konnte nicht erstellt werden' });
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (profilId) => {
    if (!window.confirm('Mitarbeiter wirklich deaktivieren?')) return;
    setSaving(true);
    setError('');
    try {
      await api.post(getApiUrl(`/api/mitarbeiter/${profilId}/deaktivieren`), {});
      await loadProfiles();
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Mitarbeiter konnte nicht deaktiviert werden');
    } finally {
      setSaving(false);
    }
  };

  const handleFreigabeStatus = async (zeitbuchungId, status) => {
    const kommentar = ['abgelehnt', 'storniert'].includes(status) ? window.prompt(status === 'storniert' ? 'Stornierungsgrund' : 'Ablehnungsgrund') || '' : '';
    try {
      await apiService.updateZeitfreigabe(zeitbuchungId, { status, kommentar });
      const result = await apiService.getZeitfreigaben();
      setFreigaben(result?.eintraege || []);
    } catch (err) {
      setFreigabenError(err?.response?.data?.error || err?.message || 'Freigabe konnte nicht gespeichert werden');
    }
  };

  const handleCreateAbrechnung = async (forceRecreate = false) => {
    if (!abrechnungsProfilId || !abrechnungsMonat) return;
    setAbrechnungSaving(true);
    setAbrechnungsError('');
    try {
      const result = await apiService.createGehaltsAbrechnung({
        profil_id: abrechnungsProfilId,
        monat: abrechnungsMonat,
        force_recreate: forceRecreate
      });
      setAbrechnungsVorschau(result?.abrechnung ? { ...abrechnungsVorschau, abrechnung: result.abrechnung } : abrechnungsVorschau);
    } catch (err) {
      const message = err?.response?.data?.error || err?.message || 'Abrechnung konnte nicht erstellt werden';
      if (err?.response?.status === 409 && !forceRecreate) {
        const confirmed = window.confirm('Für diesen Monat existiert bereits eine Abrechnung. Korrektur neu erstellen?');
        if (confirmed) {
          try {
            const retryResult = await apiService.createGehaltsAbrechnung({
              profil_id: abrechnungsProfilId,
              monat: abrechnungsMonat,
              force_recreate: true,
              notiz: 'Korrektur nach bereits abgewickelter Abrechnung'
            });
            setAbrechnungsVorschau(retryResult?.abrechnung ? { ...abrechnungsVorschau, abrechnung: retryResult.abrechnung } : abrechnungsVorschau);
            return;
          } catch (retryError) {
            setAbrechnungsError(retryError?.response?.data?.error || retryError?.message || 'Korrektur konnte nicht erstellt werden');
            return;
          }
        }
      }
      setAbrechnungsError(message);
    } finally {
      setAbrechnungSaving(false);
    }
  };

  const handleExportAbrechnung = async (format = 'csv') => {
    if (!abrechnungsVorschau?.abrechnung?.abrechnung_id) return;
    try {
      const response = await apiService.exportGehaltsAbrechnung(abrechnungsVorschau.abrechnung.abrechnung_id, format);
      const blob = new Blob([response], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `abrechnung-${abrechnungsVorschau.abrechnung.abrechnung_id}.${format === 'datev' ? 'datev.csv' : 'csv'}`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setAbrechnungsError(err?.response?.data?.error || err?.message || 'Export konnte nicht geladen werden');
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Paper sx={{ p: { xs: 2, md: 3 } }}>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2} sx={{ mb: 3 }}>
          <Box>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <PeopleOutlined sx={{ color: '#1a365d' }} />
              <Typography variant="h4" sx={{ color: '#1a365d', fontWeight: 700 }}>Mitarbeiterverwaltung</Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary">
              Profile, Einladungen und Sperren für MA-Logins.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Button variant="outlined" onClick={loadProfiles} disabled={loading || saving}>Aktualisieren</Button>
            <Button variant="text" onClick={() => { setSelectedId(null); setForm(emptyForm); setUrlaubstageManuellBearbeitet(false); }}>Neues Profil</Button>
          </Stack>
        </Stack>

        <Tabs value={activeTab} onChange={(_, value) => setActiveTab(value)} sx={{ mb: 3 }}>
          <Tab label="Mitarbeiter" />
          <Tab label="Freigaben" />
          <Tab label="Abrechnung" />
          <Tab label="Scan-Links" />
          <Tab label="Dienstplan" />
        </Tabs>

        {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

        {activeTab === 1 ? (
          <Stack spacing={2} sx={{ mb: 3 }}>
            {freigabenError ? <Alert severity="error">{freigabenError}</Alert> : null}
            {freigabenLoading ? <Alert severity="info">Lade Freigaben ...</Alert> : null}
            {freigaben.map((entry) => (
              <Card key={entry.zeitbuchung_id} variant="outlined">
                <CardContent>
                  <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2}>
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        {entry.vorname} {entry.nachname}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {entry.wirksam_am || entry.start_ts || 'ohne Datum'} · {Number(entry.dauer_minuten || 0)} Minuten · {entry.taetigkeit || entry.todo_titel || 'Ohne Tätigkeit'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Status: {entry.status || 'freigegeben'} · Projekt: {entry.todo_titel || 'kein Projekt'}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={1}>
                      {entry.status === 'freigegeben' ? (
                        <Button size="small" color="warning" variant="outlined" onClick={() => handleFreigabeStatus(entry.zeitbuchung_id, 'storniert')}>Stornieren</Button>
                      ) : (
                        <>
                          <Button size="small" variant="contained" onClick={() => handleFreigabeStatus(entry.zeitbuchung_id, 'freigegeben')}>Freigeben</Button>
                          <Button size="small" color="error" variant="outlined" onClick={() => handleFreigabeStatus(entry.zeitbuchung_id, 'abgelehnt')}>Ablehnen</Button>
                        </>
                      )}
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            ))}
            {!freigabenLoading && freigaben.length === 0 ? <Alert severity="info">Keine offenen oder direkt gebuchten Zeitbuchungen vorhanden.</Alert> : null}
          </Stack>
        ) : null}

        {activeTab === 2 ? (
          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <Stack spacing={2}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Monatsabrechnung</Typography>
              {abrechnungsError ? <Alert severity="error">{abrechnungsError}</Alert> : null}
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField select label="Mitarbeiter" value={abrechnungsProfilId} onChange={(e) => setAbrechnungsProfilId(e.target.value)} fullWidth>
                  {profiles.map((profile) => (
                    <MenuItem key={profile.profil_id} value={profile.profil_id}>{profile.vorname} {profile.nachname}</MenuItem>
                  ))}
                </TextField>
                <TextField label="Monat" type="month" value={abrechnungsMonat} onChange={(e) => setAbrechnungsMonat(e.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
              </Stack>
              <Stack direction="row" spacing={1}>
                <Button variant="contained" onClick={() => handleCreateAbrechnung(false)} disabled={abrechnungSaving || !abrechnungsProfilId}>Abrechnung erstellen</Button>
                <Button variant="outlined" onClick={() => handleCreateAbrechnung(true)} disabled={abrechnungSaving || !abrechnungsProfilId}>Korrektur für diesen Monat erstellen</Button>
                <Button variant="outlined" onClick={() => handleExportAbrechnung('csv')} disabled={!abrechnungsVorschau?.abrechnung?.abrechnung_id}>CSV exportieren</Button>
                <Button variant="outlined" onClick={() => handleExportAbrechnung('datev')} disabled={!abrechnungsVorschau?.abrechnung?.abrechnung_id}>DATEV exportieren</Button>
              </Stack>
              {abrechnungsLoading ? <Alert severity="info">Lade Vorschau ...</Alert> : null}
              {abrechnungsVorschau?.abrechnungs_history?.length ? (
                <Box sx={{ p: 2, border: '1px solid #d0d7de', borderRadius: 2, bgcolor: '#f8fafc' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Revisionen dieses Monats</Typography>
                  <Stack spacing={1}>
                    {abrechnungsVorschau.abrechnungs_history.slice(0, 3).map((entry) => (
                      <Box key={entry.abrechnung_id} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', borderBottom: '1px solid #e5e7eb', pb: 0.5 }}>
                        <Typography variant="body2">
                          {entry.status === 'final' ? 'Aktuelle Revision' : 'Vorherige Version'} · #{entry.abrechnung_id}
                        </Typography>
                        <Typography variant="body2">
                          {Number(entry.summe_minuten || 0) / 60} h · {((Number(entry.brutto_cent || 0)) / 100).toFixed(2)} €
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              ) : null}
              {abrechnungsVorschau ? (
                <Alert severity="success">
                  Vorschau: {Number(abrechnungsVorschau.summe_stunden || 0).toFixed(2)} h · {((Number(abrechnungsVorschau.brutto_cent || 0)) / 100).toFixed(2)} €
                </Alert>
              ) : null}
            </Stack>
          </Paper>
        ) : null}

        {activeTab === 3 ? (
          <Stack spacing={2} sx={{ mb: 3 }}>
            <Alert severity="info">
              Pro Task gibt es je einen Link für Kommen und Gehen. Die Links können als QR-Code gedruckt werden
              und bleiben dauerhaft gültig. Buchung erfolgt ohne Login über die Mitarbeiter-PIN;
              die Einträge erscheinen anschließend im Freigaben-Tab.
            </Alert>
            {scanLinksError ? <Alert severity="error">{scanLinksError}</Alert> : null}
            {scanLinksLoading ? <Alert severity="info">Lade Scan-Links ...</Alert> : null}
            {scanLinks.map((task) => (
              <Card key={task.id} variant="outlined">
                <CardContent>
                  <Stack spacing={1}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{task.titel}</Typography>
                      <Chip size="small" label={task.status || 'offen'} />
                      {task.zugewiesen_an ? <Chip size="small" variant="outlined" label={task.zugewiesen_an} /> : null}
                    </Stack>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
                      <Button
                        size="small"
                        variant="outlined"
                        color="success"
                        onClick={() => handleCopyScanLink(toAbsoluteScanUrl(task.checkin_url), `${task.id}-in`)}
                      >
                        {copiedLink === `${task.id}-in` ? 'Kopiert!' : 'Kommen-Link kopieren'}
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        onClick={() => handleCopyScanLink(toAbsoluteScanUrl(task.checkout_url), `${task.id}-out`)}
                      >
                        {copiedLink === `${task.id}-out` ? 'Kopiert!' : 'Gehen-Link kopieren'}
                      </Button>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            ))}
            {!scanLinksLoading && scanLinks.length === 0 ? <Alert severity="info">Keine offenen Tasks vorhanden.</Alert> : null}
          </Stack>
        ) : null}

        {activeTab === 4 ? (
          <Stack spacing={2} sx={{ mb: 3 }}>
            <Alert severity="info">
              Klicke auf einen Tag, um Schichten freizuschalten (Ort, optionale Zeiten, optionales Platz-Limit).
              Mitarbeiter tragen sich über den öffentlichen Dienstplan-Link per PIN pro Schicht ein; öffentlich ist
              nur die Anzahl der Anmeldungen sichtbar, Namen siehst nur du hier.
            </Alert>
            {dienstplanError ? <Alert severity="error">{dienstplanError}</Alert> : null}
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                disabled={!dienstplanLink}
                onClick={() => handleCopyScanLink(dienstplanLink.startsWith('http') ? dienstplanLink : `${window.location.origin}${dienstplanLink}`, 'dienstplan')}
              >
                {copiedLink === 'dienstplan' ? 'Kopiert!' : 'Dienstplan-Link kopieren'}
              </Button>
            </Stack>
            {dienstplanLoading ? <Alert severity="info">Lade Dienstplan ...</Alert> : null}

            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ maxWidth: 560 }}>
              <IconButton onClick={() => setDienstplanMonat((prev) => { const d = new Date(prev.year, prev.month - 1, 1); return { year: d.getFullYear(), month: d.getMonth() }; })} aria-label="Vorheriger Monat"><ChevronLeft /></IconButton>
              <Typography variant="h6">{monatsLabel(dienstplanMonat.year, dienstplanMonat.month)}</Typography>
              <IconButton onClick={() => setDienstplanMonat((prev) => { const d = new Date(prev.year, prev.month + 1, 1); return { year: d.getFullYear(), month: d.getMonth() }; })} aria-label="Nächster Monat"><ChevronRight /></IconButton>
            </Stack>

            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5, maxWidth: 560 }}>
              {WOCHENTAGE_KURZ.map((wt) => (
                <Typography key={wt} variant="caption" sx={{ textAlign: 'center', fontWeight: 700, color: 'text.secondary' }}>{wt}</Typography>
              ))}
              {buildMonthGrid(dienstplanMonat.year, dienstplanMonat.month).flat().map((cell, idx) => {
                if (!cell) return <Box key={`leer-${idx}`} />;
                const tagSchichten = dienstplanSchichten.filter((s) => s.datum === cell.iso);
                const vergangen = cell.iso < todayIso();
                const vornamen = tagSchichten.flatMap((s) => (s.anmeldungen || []).map((a) => a.vorname || a.nachname || `#${a.profil_id}`));
                const tooltipText = tagSchichten
                  .filter((s) => (s.anmeldungen || []).length > 0)
                  .map((s) => `${s.ort}${formatZeit(s) ? ` (${formatZeit(s)})` : ''}: ${(s.anmeldungen || []).map((a) => {
                    const name = `${a.vorname || ''} ${a.nachname || ''}`.trim();
                    return a.zeit_von ? `${name} (${formatZeit(a)})` : name;
                  }).join(', ')}`)
                  .join('\n');
                return (
                  <Tooltip key={cell.iso} title={tooltipText ? <span style={{ whiteSpace: 'pre-line' }}>{tooltipText}</span> : ''} arrow disableInteractive>
                  <Box
                    onClick={() => !vergangen && setTagDialog({ datum: cell.iso, form: { ...leeresSchichtForm } })}
                    sx={{
                      aspectRatio: '1 / 1',
                      borderRadius: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 0.25,
                      border: '1px solid',
                      borderColor: tagSchichten.length > 0 ? 'primary.main' : 'divider',
                      bgcolor: vergangen ? 'grey.100' : tagSchichten.length > 0 ? 'primary.50' : 'background.paper',
                      opacity: vergangen ? 0.5 : 1,
                      cursor: vergangen ? 'default' : 'pointer',
                      '&:hover': vergangen ? {} : { boxShadow: 2 }
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: tagSchichten.length > 0 ? 700 : 400 }}>{cell.day}</Typography>
                    {tagSchichten.length > 0 ? (
                      <Typography variant="caption" sx={{ lineHeight: 1 }}>
                        {tagSchichten.length > 1
                          ? `${tagSchichten.length} Schichten`
                          : `${(tagSchichten[0].anmeldungen || []).length}${tagSchichten[0].max_plaetze !== null ? `/${tagSchichten[0].max_plaetze}` : ''}`}
                      </Typography>
                    ) : null}
                    {vornamen.length > 0 ? (
                      <Typography variant="caption" sx={{ lineHeight: 1.1, fontSize: '0.6rem', color: 'text.secondary', maxWidth: '95%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {vornamen.join(', ')}
                      </Typography>
                    ) : null}
                  </Box>
                  </Tooltip>
                );
              })}
            </Box>

            {dienstplanSchichten.filter((s) => (s.anmeldungen || []).length > 0 && s.datum >= todayIso()).map((s) => (
              <Card key={s.schicht_id} variant="outlined" sx={{ maxWidth: 560 }}>
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    {new Date(`${s.datum}T00:00:00`).toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric' })}
                    {` · ${s.ort}`}
                    {formatZeit(s) ? ` · ${formatZeit(s)}` : ''}
                    {s.notiz ? ` · ${s.notiz}` : ''}
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                    {(s.anmeldungen || []).map((a) => {
                      const name = `${a.vorname || ''} ${a.nachname || ''}`.trim() || `Profil ${a.profil_id}`;
                      return <Chip key={a.anmeldung_id} size="small" label={a.zeit_von ? `${name} (${formatZeit(a)})` : name} />;
                    })}
                  </Stack>
                </CardContent>
              </Card>
            ))}

            <Dialog open={Boolean(tagDialog)} onClose={() => !tagSaving && setTagDialog(null)} maxWidth="xs" fullWidth>
              <DialogTitle>
                Schichten
                {tagDialog ? ` – ${new Date(`${tagDialog.datum}T00:00:00`).toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric' })}` : ''}
              </DialogTitle>
              <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }}>
                  {dienstplanSchichten.filter((s) => s.datum === tagDialog?.datum).map((s) => (
                    <Stack key={s.schicht_id} direction="row" alignItems="center" spacing={1} sx={{ border: '1px solid', borderColor: tagDialog?.form?.schichtId === s.schicht_id ? 'primary.main' : 'divider', borderRadius: 1, px: 1.5, py: 1 }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{s.ort}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatZeit(s) ? `${formatZeit(s)} · ` : ''}
                          {(s.anmeldungen || []).length}{s.max_plaetze !== null ? `/${s.max_plaetze}` : ''} angemeldet
                        </Typography>
                      </Box>
                      <IconButton
                        size="small"
                        aria-label="Schicht bearbeiten"
                        onClick={() => setTagDialog((prev) => ({
                          ...prev,
                          form: {
                            schichtId: s.schicht_id,
                            ort: s.ort || '',
                            zeitVon: s.zeit_von || '',
                            zeitBis: s.zeit_bis || '',
                            maxPlaetze: s.max_plaetze ?? '',
                            notiz: s.notiz || ''
                          }
                        }))}
                      >
                        <EditOutlined fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" aria-label="Schicht löschen" onClick={() => handleSchichtLoeschen(s)} disabled={tagSaving}>
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    </Stack>
                  ))}

                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    {tagDialog?.form?.schichtId ? 'Schicht bearbeiten' : 'Neue Schicht'}
                  </Typography>
                  <TextField
                    label="Ort"
                    value={tagDialog?.form?.ort ?? ''}
                    onChange={(e) => setTagDialog((prev) => ({ ...prev, form: { ...prev.form, ort: e.target.value } }))}
                    fullWidth
                    required
                  />
                  <Stack direction="row" spacing={1}>
                    <TextField
                      label="Beginn"
                      type="time"
                      value={tagDialog?.form?.zeitVon ?? ''}
                      onChange={(e) => setTagDialog((prev) => ({ ...prev, form: { ...prev.form, zeitVon: e.target.value } }))}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      required
                    />
                    <TextField
                      label="Ende"
                      type="time"
                      value={tagDialog?.form?.zeitBis ?? ''}
                      onChange={(e) => setTagDialog((prev) => ({ ...prev, form: { ...prev.form, zeitBis: e.target.value } }))}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                    />
                  </Stack>
                  <TextField
                    label="Platz-Limit (leer = unbegrenzt)"
                    value={tagDialog?.form?.maxPlaetze ?? ''}
                    onChange={(e) => setTagDialog((prev) => ({ ...prev, form: { ...prev.form, maxPlaetze: e.target.value.replace(/\D/g, '') } }))}
                    inputProps={{ inputMode: 'numeric' }}
                    fullWidth
                  />
                  <TextField
                    label="Notiz (optional, öffentlich sichtbar)"
                    value={tagDialog?.form?.notiz ?? ''}
                    onChange={(e) => setTagDialog((prev) => ({ ...prev, form: { ...prev.form, notiz: e.target.value } }))}
                    fullWidth
                    multiline
                    minRows={2}
                  />
                </Stack>
              </DialogContent>
              <DialogActions>
                {tagDialog?.form?.schichtId ? (
                  <Button onClick={() => setTagDialog((prev) => ({ ...prev, form: { ...leeresSchichtForm } }))} disabled={tagSaving}>
                    Neue Schicht
                  </Button>
                ) : null}
                <Box sx={{ flex: 1 }} />
                <Button onClick={() => setTagDialog(null)} disabled={tagSaving}>Schließen</Button>
                <Button variant="contained" onClick={handleSchichtSpeichern} disabled={tagSaving}>
                  {tagDialog?.form?.schichtId ? 'Speichern' : 'Schicht freischalten'}
                </Button>
              </DialogActions>
            </Dialog>
          </Stack>
        ) : null}

        {activeTab === 0 ? <Grid container spacing={2}>
          <Grid item xs={12} lg={7}>
            <Stack spacing={2}>
              {loading ? <Alert severity="info">Lade Mitarbeiter ...</Alert> : null}
              {profiles.map((profile) => (
                <Card key={profile.profil_id} variant="outlined" sx={{ borderColor: selectedId === profile.profil_id ? '#1a365d' : 'divider' }}>
                  <CardContent>
                    <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2}>
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                          {profile.vorname} {profile.nachname}
                        </Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
                          <Chip size="small" label={profile.aktiv ? 'aktiv' : 'inaktiv'} color={profile.aktiv ? 'success' : 'default'} />
                          <Chip size="small" variant="outlined" label={profile.login_email ? 'Login verknüpft' : 'kein Login'} />
                          <Chip size="small" variant="outlined" label={profile.membership_role || 'viewer'} />
                          <Chip size="small" variant="outlined" label={profile.planner_ressource || 'keine Planner-Ressource'} />
                        </Stack>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          {profile.personalnummer ? `Personalnummer: ${profile.personalnummer} · ` : ''}
                          {profile.wochenstunden ?? 40} h/Woche · {profile.arbeitstage_pro_woche ?? 5} Arbeitstage/Woche
                        </Typography>
                        {profile.einladung_email ? (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            Einladung: {profile.einladung_email} · {profile.invitation_status || '—'}
                          </Typography>
                        ) : null}
                      </Box>
                      <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="flex-start">
                        <Button size="small" startIcon={<EditOutlined />} onClick={() => setSelectedId(profile.profil_id)}>Bearbeiten</Button>
                        <Button size="small" startIcon={<EmailOutlined />} onClick={() => handleInvite(profile.profil_id)}>Einladen</Button>
                        {profile.einladung_email && profile.invitation_status && profile.invitation_status !== 'accepted' ? (
                          <Button size="small" disabled={saving} onClick={() => handleInvite(profile.profil_id, profile.einladung_email)}>
                            Link erneut erzeugen
                          </Button>
                        ) : null}
                        <Button size="small" color="error" startIcon={<BlockOutlined />} onClick={() => handleDeactivate(profile.profil_id)}>Deaktivieren</Button>
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              ))}
              {!loading && profiles.length === 0 ? <Alert severity="info">Noch keine Mitarbeiter angelegt.</Alert> : null}
            </Stack>
          </Grid>

          <Grid item xs={12} lg={5}>
            <Paper variant="outlined" sx={{ p: 2, position: 'sticky', top: 24 }} component="form" onSubmit={handleSubmit}>
              <Stack spacing={2}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {selectedId ? 'Mitarbeiter bearbeiten' : 'Mitarbeiter anlegen'}
                </Typography>
                <Divider />
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <TextField label="Vorname" value={form.vorname} onChange={handleChange('vorname')} fullWidth required />
                  <TextField label="Nachname" value={form.nachname} onChange={handleChange('nachname')} fullWidth required />
                </Stack>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <TextField label="Personalnummer (optional, auto wenn leer)" value={form.personalnummer} onChange={handleChange('personalnummer')} fullWidth />
                  <TextField label="DATEV-Personalnummer" value={form.datev_personalnummer} onChange={handleChange('datev_personalnummer')} fullWidth />
                </Stack>
                <TextField label="E-Mail für Einladung" value={form.einladung_email} onChange={handleChange('einladung_email')} fullWidth />
                <TextField label="Planner-Ressource" value={form.planner_ressource} onChange={handleChange('planner_ressource')} fullWidth helperText="Muss mit dem Ressourcen-Kalender-Label übereinstimmen." />
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <TextField label="Eintrittsdatum" type="date" value={form.eintrittsdatum} onChange={handleChange('eintrittsdatum')} InputLabelProps={{ shrink: true }} fullWidth />
                  <TextField label="Austrittsdatum" type="date" value={form.austrittsdatum} onChange={handleChange('austrittsdatum')} InputLabelProps={{ shrink: true }} fullWidth />
                </Stack>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <TextField label="Stundenlohn (EUR oder Cent)" value={form.stundenlohn_cent} onChange={handleChange('stundenlohn_cent')} fullWidth helperText="Beispiele: 14,70 oder 1470" />
                  <TextField label="Wochenstunden" type="number" value={form.wochenstunden} onChange={handleChange('wochenstunden')} fullWidth />
                </Stack>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <TextField label="Arbeitstage/Woche" type="number" value={form.arbeitstage_pro_woche} onChange={handleChange('arbeitstage_pro_woche')} fullWidth />
                  <TextField
                    label="Urlaubstage/Jahr"
                    type="number"
                    value={form.urlaubstage_pro_jahr}
                    onChange={handleChange('urlaubstage_pro_jahr')}
                    fullWidth
                    helperText={`Mindestwert: ${getMinimumVacationDays(form.arbeitstage_pro_woche)}`}
                  />
                </Stack>
                <TextField label="Beschäftigungsart" value={form.beschaeftigungsart} onChange={handleChange('beschaeftigungsart')} fullWidth />
                <TextField
                  label="Zeiterfassungs-PIN (6-stellig)"
                  value={form.zeiterfassung_pin}
                  onChange={(e) => setForm((prev) => ({ ...prev, zeiterfassung_pin: e.target.value.replace(/\D/g, '').slice(0, 6) }))}
                  inputProps={{ inputMode: 'numeric', maxLength: 6 }}
                  fullWidth
                  helperText="Für Scan-Buchungen ohne Login. Muss je Mandant eindeutig sein."
                />
                <Button type="submit" variant="contained" disabled={saving} startIcon={<AddCircleOutline />}>
                  {selectedId ? 'Speichern' : 'Anlegen'}
                </Button>
              </Stack>
            </Paper>
          </Grid>
        </Grid> : null}
      </Paper>

      <Dialog open={Boolean(inviteLink)} onClose={() => setInviteLink(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{inviteLink?.error ? 'Einladung fehlgeschlagen' : 'Einladungslink teilen'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            {inviteLink?.error ? (
              <Alert severity="error">
                Einladung für <strong>{inviteLink?.email}</strong> nicht möglich: {inviteLink.error}
              </Alert>
            ) : (
              <>
                <Alert severity="success">
                  Die Einladung für <strong>{inviteLink?.email}</strong> wurde erstellt.
                  Falls der E-Mail-Versand konfiguriert ist, wurde sie zusätzlich per E-Mail verschickt.
                </Alert>
                <Typography variant="body2" color="text.secondary">
                  Du kannst den Link auch direkt per WhatsApp, SMS oder Messenger weitergeben.
                  {(inviteLink?.email || '').toLowerCase() === PLATZHALTER_EMAIL
                    ? ' Platzhalter-Einladung: Der Mitarbeiter registriert sich mit seiner eigenen E-Mail-Adresse.'
                    : <> Wichtig: Die Registrierung muss mit der E-Mail-Adresse <strong>{inviteLink?.email}</strong> erfolgen.</>}
                  {' '}Der Link ist 24 Stunden gültig.
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <TextField
                    value={inviteLink?.url || ''}
                    size="small"
                    fullWidth
                    InputProps={{ readOnly: true }}
                    onFocus={(e) => e.target.select()}
                  />
                  <Button
                    variant="contained"
                    onClick={() => handleCopyScanLink(inviteLink?.url || '', 'einladung')}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    {copiedLink === 'einladung' ? 'Kopiert!' : 'Kopieren'}
                  </Button>
                </Stack>
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInviteLink(null)}>Schließen</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserManagement;
